import { computed } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { useCustomToast, useExcelBlobExport } from '@/shared/lib';
import {
  permissionApi,
  type PermissionCategory,
  type PermissionMatrixPositionType,
} from '@/entities/Permission';

type CurrentSort = { sort: string; order: string } | null;

type UsePermissionsExportParams = {
  categoriesFiltered: ComputedRef<PermissionCategory[]>;
  searchTerm: Ref<string>;
  selectedCategoryFilter: Ref<number | null>;
  selectedPositionType: Ref<PermissionMatrixPositionType>;
  currentSort?: Ref<CurrentSort>;
};

export const usePermissionsExport = ({
  categoriesFiltered,
  searchTerm,
  selectedCategoryFilter,
  selectedPositionType,
  currentSort,
}: UsePermissionsExportParams) => {
  const toast = useCustomToast();

  const hasExportItems = computed(() =>
    categoriesFiltered.value.some((category) => category.positions.length > 0)
  );

  const notifySaveSuccess = () => {
    toast.showSuccessToast({
      title: 'Permissões salvas com sucesso',
      message: 'Os usuários envolvidos serão notificados.',
      type: 'success',
    });
  };

  const notifySaveError = (message?: string) => {
    toast.showErrorToast({
      title: 'Erro ao salvar permissões',
      message: message ?? 'Houve um erro ao salvar as permissões, tente novamente mais tarde.',
      type: 'error',
    });
  };

  const { handleExportToExcel, isExporting } = useExcelBlobExport(
    () =>
      permissionApi.exportPermissionsExcel({
        term: searchTerm.value.trim() || undefined,
        categoryId: selectedCategoryFilter.value ?? undefined,
        positionType: selectedPositionType.value,
        sort: currentSort?.value?.sort,
        order: currentSort?.value?.order,
      }),
    { singular: 'cargo', plural: 'cargos' }
  );

  return {
    handleExportToExcel,
    isExporting,
    hasExportItems,
    notifySaveSuccess,
    notifySaveError,
  };
};
